/* ============================================================
   Stories Widget — player behavior
   Vanilla custom element. No deps. Lazy media loading.
   ============================================================ */
(function () {
  if (window.customElements && customElements.get('stories-widget')) return;

  class StoriesWidget extends HTMLElement {
    constructor() {
      super();
      this.stories = [];
      this.index = 0;
      this.modal = null;
      this.viewport = null;
      this.progressEl = null;
      this.pauseBtn = null;
      this.muteBtn = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.activeMedia = null;
      this.activeOverlay = null;
      this.activeTimerId = 0;
      this.activeStartTime = 0;
      this.activeRemaining = 0;
      this.paused = false;
      this.muted = true;
      this.loop = false;
      this.autoadvance = true;
      this.swipeEnabled = true;
      this.imageDuration = 6;
      this._onKey = this._onKey.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
    }

    connectedCallback() {
      this.imageDuration = parseFloat(this.dataset.imageDuration) || 6;
      this.muted         = this.dataset.muteDefault !== 'false';
      this.loop          = this.dataset.loop === 'true';
      this.autoadvance   = this.dataset.autoadvance !== 'false';
      this.swipeEnabled  = this.dataset.swipe !== 'false';

      // Collect story data from <template> children — inert, no loads.
      this.stories = Array.from(this.querySelectorAll('.stories-story-data')).map((tpl) => {
        const ds = tpl.dataset;
        // First child is either <img> or <video>
        const mediaNode = tpl.content && tpl.content.firstElementChild
          ? tpl.content.firstElementChild
          : null;
        return {
          type:         ds.type || 'image',
          duration:     parseFloat(ds.duration) || 0,
          title:        ds.title || '',
          subtitle:     ds.subtitle || '',
          textPosition: ds.textPosition || 'bottom-left',
          textColor:    ds.textColor || '#FFFFFF',
          ctaLabel:     ds.ctaLabel || '',
          ctaUrl:       ds.ctaUrl || '',
          linkUrl:      ds.linkUrl || '',
          mediaNode:    mediaNode,
        };
      });

      this.trigger    = this.querySelector('[data-stories-trigger]');
      this.modal      = this.querySelector('[data-stories-modal]');
      this.viewport   = this.querySelector('[data-stories-viewport]');
      this.progressEl = this.querySelector('[data-stories-progress]');
      this.pauseBtn   = this.querySelector('[data-stories-pause]');
      this.muteBtn    = this.querySelector('[data-stories-mute]');
      this.prevBtn    = this.querySelector('[data-stories-prev]');
      this.nextBtn    = this.querySelector('[data-stories-next]');

      if (!this.trigger || !this.modal || !this.stories.length) return;

      // === Escape the containing block ===
      // Theme groups often apply transform/filter/will-change to their
      // wrappers, which traps position:fixed descendants inside that box
      // instead of pinning them to the viewport. Moving the modal to
      // document.body ensures the backdrop and player truly fill the
      // viewport.
      if (this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }

      // === Inject the trigger into the header icon row ===
      // When `place_in_header` is on, slot the button before the search
      // icon inside .header__icons. That puts it in the natural flex
      // layout, so it aligns with cart/account/search across viewports.
      if (this.dataset.placeInHeader === 'true') {
        this._injectIntoHeader();
      } else {
        // Floating-mode: no DOM movement needed, button is in its final
        // spot already. Reveal it now (the CSS hides it by default to
        // avoid the inject flicker).
        this.trigger.classList.add('is-ready');
      }

      // Reflect default mute state
      this.muteBtn?.classList.toggle('is-unmuted', !this.muted);

      // Event wiring
      this.trigger.addEventListener('click', () => this.open(0));

      // IMPORTANT: query from `this.modal`, NOT from `this`. After the portal
      // above, the modal is no longer a descendant of <stories-widget>, so
      // `this.querySelectorAll(...)` would find nothing.
      this.modal.querySelectorAll('[data-stories-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          // Backdrop click should close, but only if directly clicked
          if (el.classList.contains('stories-modal__backdrop') && e.target !== el) return;
          this.close();
        });
      });
      this.modal.querySelectorAll('[data-stories-prev]').forEach((el) => el.addEventListener('click', () => this.prev()));
      this.modal.querySelectorAll('[data-stories-next]').forEach((el) => el.addEventListener('click', () => this.next()));
      this.pauseBtn?.addEventListener('click', () => this.togglePause());
      this.muteBtn?.addEventListener('click', () => this.toggleMute());

      // Build progress bar segments once (re-used on each open)
      this._buildProgress();
    }

    disconnectedCallback() {
      // The trigger and modal may have been portaled OUT of this element
      // (into .header__icons / .header__left-icons / document.body). When
      // this <stories-widget> is removed — e.g. the theme editor re-renders
      // the section after a settings change — those portaled elements would
      // otherwise be left orphaned, causing duplicate buttons / leftover
      // modals. Clean them up here.
      if (this.trigger && this.trigger.parentNode && this.trigger.parentNode !== this) {
        this.trigger.remove();
      }
      if (this.modal && this.modal.parentNode === document.body) {
        // Make sure the modal isn't visible when removed
        this.modal.hidden = true;
        document.body.classList.remove('stories-modal-open');
        this.modal.remove();
      }
      // Drop the viewport-change listener that was added in _injectIntoHeader
      if (this._mqlRef && this._mqlChangeHandler) {
        this._mqlRef.removeEventListener('change', this._mqlChangeHandler);
        this._mqlChangeHandler = null;
        this._mqlRef = null;
      }
      this._removeGlobalListeners();
    }

    _injectIntoHeader() {
      const sectionId = this.trigger.dataset.storiesSectionId;
      const mql = window.matchMedia('(max-width: 749px)');

      const place = () => {
        const isMobile = mql.matches;

        // Pick target container based on viewport.
        // On mobile, sit beside the hamburger (in the same row as the mobile
        // account icon). On desktop, sit before the search in the right row.
        let target = null;
        if (isMobile) {
          target = document.querySelector('.header__left-icons')
                || document.querySelector('.header__icons');
        } else {
          target = document.querySelector('.header__icons');
        }
        if (!target) return false;

        // Clean up any stale clones of THIS section's trigger anywhere in the
        // document (defensive, in case disconnectedCallback didn't run).
        if (sectionId) {
          document.querySelectorAll(
            '[data-stories-section-id="' + sectionId + '"]'
          ).forEach((stale) => {
            if (stale !== this.trigger) stale.remove();
          });
        }

        // Already in the right container? Nothing to do.
        if (this.trigger.parentNode === target) return true;

        if (isMobile) {
          // Append at the END of left-icons. The mobile account icon usually
          // sits there too — if the user hides it via CSS, this takes its
          // visual spot. Otherwise both show, hamburger → account → stories.
          target.appendChild(this.trigger);
        } else {
          const searchEl = target.querySelector('.header__search')
                        || target.querySelector('.header__icon--search');
          if (searchEl) {
            target.insertBefore(this.trigger, searchEl);
          } else {
            target.prepend(this.trigger);
          }
        }
        this.trigger.classList.add('stories-trigger--inline');
        // Now in its final position — safe to reveal
        this.trigger.classList.add('is-ready');
        return true;
      };

      // Retry up to ~3 seconds in case the header mounts late.
      let attempts = 0;
      const tryPlace = () => {
        attempts++;
        if (place()) return;
        if (attempts < 12) setTimeout(tryPlace, 250);
      };
      tryPlace();

      // Re-inject on viewport change so mobile/desktop placement always matches.
      this._mqlChangeHandler = () => place();
      mql.addEventListener('change', this._mqlChangeHandler);
      this._mqlRef = mql;
    }

    _buildProgress() {
      if (!this.progressEl) return;
      this.progressEl.innerHTML = '';
      for (let i = 0; i < this.stories.length; i++) {
        const seg = document.createElement('div');
        seg.className = 'stories-modal__progress-segment';
        const fill = document.createElement('div');
        fill.className = 'stories-modal__progress-fill';
        seg.appendChild(fill);
        this.progressEl.appendChild(seg);
      }
    }

    open(index) {
      if (!this.stories.length) return;
      this.index = Math.max(0, Math.min(index || 0, this.stories.length - 1));
      this.modal.hidden = false;
      document.body.classList.add('stories-modal-open');
      this._addGlobalListeners();
      // Move focus into the modal
      this.pauseBtn?.focus({ preventScroll: true });
      this.show(this.index);
    }

    close() {
      this._clearTimer();
      this._cleanupActive();
      this.modal.hidden = true;
      document.body.classList.remove('stories-modal-open');
      this._removeGlobalListeners();
      this.trigger?.focus({ preventScroll: true });
    }

    next() {
      if (this.index < this.stories.length - 1) {
        this.show(this.index + 1);
      } else if (this.loop) {
        this.show(0);
      } else {
        this.close();
      }
    }

    prev() {
      if (this.index > 0) this.show(this.index - 1);
    }

    show(i) {
      this._clearTimer();
      this._cleanupActive();
      this.index = i;
      const story = this.stories[i];
      if (!story) return;

      // Reset progress bars: complete preceding, clear later
      const segs = this.progressEl.querySelectorAll('.stories-modal__progress-segment');
      segs.forEach((seg, idx) => {
        seg.classList.remove('is-active', 'is-complete', 'is-paused');
        const fill = seg.querySelector('.stories-modal__progress-fill');
        if (idx < i) {
          seg.classList.add('is-complete');
          if (fill) fill.style.transform = 'scaleX(1)';
        } else if (fill) {
          fill.style.transform = 'scaleX(0)';
        }
      });

      // Render media into viewport
      this.viewport.innerHTML = '';
      const mediaNode = story.mediaNode ? story.mediaNode.cloneNode(true) : null;
      if (!mediaNode) return;

      this.activeMedia = mediaNode;
      this.viewport.appendChild(mediaNode);

      // Render overlay
      this.activeOverlay = this._buildOverlay(story);
      if (this.activeOverlay) this.viewport.appendChild(this.activeOverlay);

      // Disable prev/next at edges
      if (this.prevBtn) this.prevBtn.disabled = (i === 0);
      if (this.nextBtn) this.nextBtn.disabled = (i === this.stories.length - 1 && !this.loop);

      // Resolve duration and start playback
      let duration = story.duration > 0 ? story.duration : null;

      if (story.type === 'video' && mediaNode.tagName === 'VIDEO') {
        // Lazy attach the real src
        const src = mediaNode.dataset.src;
        if (src && !mediaNode.querySelector('source')) {
          const source = document.createElement('source');
          source.src = src;
          source.type = 'video/mp4';
          mediaNode.appendChild(source);
        }
        mediaNode.muted = this.muted;
        mediaNode.preload = 'auto';
        mediaNode.playsInline = true;

        const startPlayback = () => {
          if (!duration) duration = Math.max(2, mediaNode.duration || this.imageDuration);
          this._startSegment(i, duration);
          const playPromise = mediaNode.play();
          if (playPromise && playPromise.catch) playPromise.catch(() => {});
        };
        if (mediaNode.readyState >= 1) {
          startPlayback();
        } else {
          mediaNode.addEventListener('loadedmetadata', startPlayback, { once: true });
        }
        // If video ends, advance (works even if duration was a guess)
        mediaNode.addEventListener('ended', () => {
          if (this.autoadvance && !this.paused) this.next();
        }, { once: true });
      } else {
        // Image story
        if (!duration) duration = this.imageDuration;
        this._startSegment(i, duration);
      }

      // Preload NEXT story's media in the background (light touch)
      const nextStory = this.stories[i + 1];
      if (nextStory && nextStory.mediaNode) {
        // For images, append into a hidden div to warm the cache
        if (nextStory.type === 'image') {
          const probe = nextStory.mediaNode.cloneNode(true);
          probe.loading = 'eager';
          probe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
          probe.addEventListener('load', () => probe.remove(), { once: true });
          probe.addEventListener('error', () => probe.remove(), { once: true });
          this.viewport.appendChild(probe);
        }
        // Don't preload videos — too heavy
      }
    }

    _buildOverlay(story) {
      const hasText = story.title || story.subtitle || story.ctaLabel || story.linkUrl;
      if (!hasText) return null;

      const overlay = document.createElement('div');
      overlay.className = 'stories-overlay stories-overlay--' + story.textPosition;
      if (story.textColor) overlay.style.color = story.textColor;

      // Block-level link (whole story is tappable) — sits at bottom z, behind CTA
      if (story.linkUrl) {
        const blockLink = document.createElement('a');
        blockLink.className = 'stories-overlay__blocklink';
        blockLink.href = story.linkUrl;
        blockLink.textContent = story.title || 'Open';
        overlay.appendChild(blockLink);
      }

      const inner = document.createElement('div');
      inner.className = 'stories-overlay__inner';

      if (story.title) {
        const h = document.createElement('h2');
        h.className = 'stories-overlay__title';
        h.textContent = story.title;
        inner.appendChild(h);
      }
      if (story.subtitle) {
        const p = document.createElement('p');
        p.className = 'stories-overlay__subtitle';
        p.textContent = story.subtitle;
        inner.appendChild(p);
      }
      if (story.ctaLabel && story.ctaUrl) {
        const a = document.createElement('a');
        a.className = 'stories-overlay__cta';
        a.href = story.ctaUrl;
        a.textContent = story.ctaLabel;
        inner.appendChild(a);
      }
      overlay.appendChild(inner);
      return overlay;
    }

    _startSegment(i, durationSec) {
      const segs = this.progressEl.querySelectorAll('.stories-modal__progress-segment');
      const seg = segs[i];
      if (!seg) return;

      // Cancel any prior animation / timer before starting a new one
      this._clearTimer();

      seg.classList.add('is-active');
      const fill = seg.querySelector('.stories-modal__progress-fill');
      if (!fill) return;

      // Reset fill to empty
      fill.style.transform = 'scaleX(0)';

      const totalMs = Math.max(500, durationSec * 1000);
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // For reduced-motion users: skip the visual, just show full and use timer
      if (prefersReduced) {
        fill.style.transform = 'scaleX(1)';
        this.activeTimerId = setTimeout(() => {
          if (this.autoadvance && !this.paused) this.next();
        }, totalMs + 50);
        return;
      }

      // rAF-driven progress fill — no CSS animation flakiness.
      // Tracks elapsed real time; pauses by accumulating paused duration.
      this._segIndex = i;
      this._segFill  = fill;
      this._segStart = performance.now();
      this._segTotal = totalMs;
      this._segPausedSince = null;
      this._segPausedAccum = 0;

      const tick = (now) => {
        // Bail if the segment changed (next/prev/close happened)
        if (this._segIndex !== i || this._segFill !== fill) return;

        // While paused, freeze the fill at its current value
        if (this.paused) {
          if (this._segPausedSince === null) this._segPausedSince = now;
          this._rafId = requestAnimationFrame(tick);
          return;
        }

        // Resuming from pause: accumulate paused time
        if (this._segPausedSince !== null) {
          this._segPausedAccum += (now - this._segPausedSince);
          this._segPausedSince = null;
        }

        const elapsed = now - this._segStart - this._segPausedAccum;
        const progress = Math.max(0, Math.min(1, elapsed / totalMs));
        fill.style.transform = 'scaleX(' + progress + ')';

        if (progress < 1) {
          this._rafId = requestAnimationFrame(tick);
        } else {
          this._rafId = null;
          if (this.autoadvance && !this.paused) this.next();
        }
      };
      this._rafId = requestAnimationFrame(tick);
    }

    _clearTimer() {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      if (this.activeTimerId) {
        clearTimeout(this.activeTimerId);
        this.activeTimerId = 0;
      }
      // Legacy from previous animation approach — cancel if present
      if (this._animation) {
        try { this._animation.cancel(); } catch (e) {}
        this._animation = null;
      }
      this._segFill = null;
      this._segIndex = -1;
    }

    _cleanupActive() {
      if (this.activeMedia && this.activeMedia.tagName === 'VIDEO') {
        try {
          this.activeMedia.pause();
          this.activeMedia.removeAttribute('src');
          this.activeMedia.load();
        } catch (e) {}
      }
      this.activeMedia = null;
      this.activeOverlay = null;
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseBtn?.classList.toggle('is-paused', this.paused);
      this.pauseBtn?.setAttribute('aria-label', this.paused ? 'Play' : 'Pause');

      // Pause/resume the active video. The progress-bar rAF loop reads
      // this.paused on every tick — it handles itself.
      if (this.activeMedia && this.activeMedia.tagName === 'VIDEO') {
        if (this.paused) {
          this.activeMedia.pause();
        } else {
          this.activeMedia.play().catch(() => {});
        }
      }
    }

    toggleMute() {
      this.muted = !this.muted;
      this.muteBtn?.classList.toggle('is-unmuted', !this.muted);
      this.muteBtn?.setAttribute('aria-label', this.muted ? 'Unmute' : 'Mute');
      if (this.activeMedia && this.activeMedia.tagName === 'VIDEO') {
        this.activeMedia.muted = this.muted;
      }
    }

    _onKey(e) {
      if (this.modal.hidden) return;
      switch (e.key) {
        case 'Escape':    e.preventDefault(); this.close();       break;
        case 'ArrowLeft': e.preventDefault(); this.prev();        break;
        case 'ArrowRight':e.preventDefault(); this.next();        break;
        case ' ':         e.preventDefault(); this.togglePause(); break;
        case 'm':
        case 'M':         e.preventDefault(); this.toggleMute();  break;
      }
    }

    _onTouchStart(e) {
      if (!this.swipeEnabled || this.modal.hidden) return;
      const t = e.touches[0];
      this._touchStartX = t.clientX;
      this._touchStartY = t.clientY;
      this._touchTime   = Date.now();
    }
    _onTouchMove() { /* reserved for future hold-to-pause */ }
    _onTouchEnd(e) {
      if (!this.swipeEnabled || this.modal.hidden) return;
      if (this._touchStartX == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - this._touchStartX;
      const dy = t.clientY - this._touchStartY;
      const dt = Date.now() - this._touchTime;
      this._touchStartX = null;
      // Swipe right → prev; left → next; down → close
      if (dt < 600 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx > 0) this.prev(); else this.next();
      } else if (dt < 600 && dy > 90 && Math.abs(dy) > Math.abs(dx)) {
        this.close();
      }
    }

    _addGlobalListeners() {
      document.addEventListener('keydown', this._onKey);
      this.modal.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.modal.addEventListener('touchmove',  this._onTouchMove,  { passive: true });
      this.modal.addEventListener('touchend',   this._onTouchEnd,   { passive: true });
    }
    _removeGlobalListeners() {
      document.removeEventListener('keydown', this._onKey);
      if (this.modal) {
        this.modal.removeEventListener('touchstart', this._onTouchStart);
        this.modal.removeEventListener('touchmove',  this._onTouchMove);
        this.modal.removeEventListener('touchend',   this._onTouchEnd);
      }
    }
  }

  customElements.define('stories-widget', StoriesWidget);
})();
